import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { fmtDuration } from "@/lib/agenda";

export const DURATION_PRESETS = [30, 45, 60, 120, 180, 240, 300, 360, 420, 480, 540, 600, 660, 720];

export const durLabel = (m) => fmtDuration(Number(m)) || "—";

export const DurationPicker = ({ value, onChange, testid = "duration" }) => {
  const v = Number(value) || 0;
  const [mode, setMode] = useState(DURATION_PRESETS.includes(v) ? "preset" : "custom");
  // Track values we emit ourselves so our own edits don't flip the UI back to a preset.
  const lastEmitted = useRef(v);
  // Local string state so Ore/Minuti can be temporarily empty while editing (no forced leading zero).
  const [hStr, setHStr] = useState(String(Math.floor(v / 60)));
  const [mStr, setMStr] = useState(String(v % 60));
  // On the first focus of each field after entering custom mode, select-all so typing replaces the value.
  const firstFocus = useRef({ h: true, m: true });
  useEffect(() => {
    const nv = Number(value) || 0;
    if (nv !== lastEmitted.current) {
      // External value change (e.g. dialog opened with a session's duration) -> re-derive.
      lastEmitted.current = nv;
      setMode(DURATION_PRESETS.includes(nv) ? "preset" : "custom");
      setHStr(String(Math.floor(nv / 60)));
      setMStr(String(nv % 60));
      firstFocus.current = { h: true, m: true };
    }
  }, [value]);

  const clean = (s) => s.replace(/[^0-9]/g, "").replace(/^0+(?=\d)/, "");
  const emitNum = (m) => { lastEmitted.current = m; onChange(m); };
  const emitFrom = (h, m) => {
    const hn = Math.max(0, Math.min(12, h === "" ? 0 : Number(h)));
    const mn = Math.max(0, Math.min(59, m === "" ? 0 : Number(m)));
    emitNum(hn * 60 + mn);
  };
  const onHours = (e) => { let s = clean(e.target.value); if (s !== "" && Number(s) > 12) s = "12"; setHStr(s); emitFrom(s, mStr); };
  const onMins = (e) => { let s = clean(e.target.value); if (s !== "" && Number(s) > 59) s = "59"; setMStr(s); emitFrom(hStr, s); };
  const syncStr = (n) => { setHStr(String(Math.floor(n / 60))); setMStr(String(n % 60)); };
  const onFocusField = (field) => (e) => {
    if (firstFocus.current[field]) { firstFocus.current[field] = false; e.target.select(); }
  };

  return (
    <div className="space-y-2">
      <Select
        value={mode === "custom" ? "__custom__" : String(v)}
        onValueChange={(val) => { if (val === "__custom__") { setMode("custom"); syncStr(v); firstFocus.current = { h: true, m: true }; } else { setMode("preset"); syncStr(Number(val)); emitNum(Number(val)); } }}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Durata…" /></SelectTrigger>
        <SelectContent>
          {DURATION_PRESETS.map((p) => <SelectItem key={p} value={String(p)}>{durLabel(p)}</SelectItem>)}
          {mode === "preset" && v > 0 && !DURATION_PRESETS.includes(v) && <SelectItem value={String(v)}>{durLabel(v)}</SelectItem>}
          <SelectItem value="__custom__">Personalizzato…</SelectItem>
        </SelectContent>
      </Select>
      {mode === "custom" && (
        <div className="flex items-center gap-2 flex-wrap" data-testid={`${testid}-custom`}>
          <Input type="number" min="0" max="12" inputMode="numeric" className="w-16" data-testid={`${testid}-hours`}
            value={hStr} onChange={onHours} onFocus={onFocusField("h")} />
          <span className="text-xs text-muted-foreground">ore</span>
          <Input type="number" min="0" max="59" inputMode="numeric" className="w-16" data-testid={`${testid}-mins`}
            value={mStr} onChange={onMins} onFocus={onFocusField("m")} />
          <span className="text-xs text-muted-foreground">min</span>
        </div>
      )}
    </div>
  );
};
